import React, { Component } from 'react';
import PropTypes from 'prop-types';
import ReactSimpleDropdown, {
  DropdownTrigger,
  DropdownContent,
} from 'react-simple-dropdown';
import { HotKeys } from 'react-hotkeys';
import onClickOutside from 'react-onclickoutside';
import cn from 'classnames';

import _isEqual from 'lodash/isEqual';

import { annotationType } from 'types';
import EmptyView from 'components/EmptyView';
import Button from 'components/Button';
import Search from 'components/Search';
import Checkbox from 'components/Checkbox';

import MatchedAnnotationList from './components/MatchedAnnotationList';

import './style.scss';

const keyMap = {
  leave: ['esc', 'escape'],
};

export class MatchedAnnotationDrawer extends Component {
  keyHandlers = {
    leave: () => this.handleCloseDropdown(),
  };

  static propTypes = {
    matchedAnnotations: PropTypes.arrayOf(annotationType),
    toggleAll: PropTypes.func,
    toggleAnnotation: PropTypes.func,
  };

  static defaultProps = {
    matchedAnnotations: [],
  };

  state = {
    isActive: false,
    // eslint-disable-next-line react/destructuring-assignment
    matchedAnnotations: this.props.matchedAnnotations,
    searchTerm: '',
  };

  componentWillReceiveProps({ matchedAnnotations }) {
    const { matchedAnnotations: currentMatchedAnnotations } = this.props;
    if (!_isEqual(matchedAnnotations, currentMatchedAnnotations)) {
      this.setState({
        matchedAnnotations,
      });
    }
  }

  handleCloseDropdown = () => {
    this.setState({
      isActive: false,
    });
  };

  handleClickOutside = () => {
    const { isActive } = this.state;
    if (isActive) {
      this.handleCloseDropdown();
    }
  };

  handleToggle = () => {
    this.setState(({ isActive }) => ({
      isActive: !isActive,
    }));
  };

  handleToggleAll = () => {
    const { toggleAll } = this.props;
    toggleAll(this.shouldCheckAll());
  };

  handleRef = el => {
    this.dropdown = el;
  };

  // TODO: this will be moved to selectors
  filterItems = (searchTerm = '') => {
    const { matchedAnnotations } = this.props;
    this.setState({
      searchTerm,
      matchedAnnotations: matchedAnnotations.filter(annotation =>
        annotation.name.toLowerCase().includes(searchTerm),
      ),
    });
  };

  shouldCheckAll = () => {
    const { matchedAnnotations } = this.props;
    return !matchedAnnotations.some(({ excluded }) => excluded);
  };

  resetAnnotations = () => {
    const { matchedAnnotations } = this.props;
    this.setState({
      searchTerm: '',
      matchedAnnotations,
    });
  };

  renderList() {
    const { toggleAnnotation } = this.props;
    const { matchedAnnotations } = this.state;

    if (!matchedAnnotations.length) {
      return null;
    }

    return (
      <MatchedAnnotationList
        matchedAnnotations={matchedAnnotations}
        toggleAnnotation={toggleAnnotation}
      />
    );
  }

  renderEmpty() {
    const { matchedAnnotations, searchTerm } = this.state;

    if (matchedAnnotations.length) {
      return null;
    }

    return (
      <EmptyView>
        {searchTerm === '' ? (
          <p>No matched annotations found</p>
        ) : (
          <React.Fragment>
            <p>
              You have searched for entity &quot;{searchTerm}&quot;,
              {matchedAnnotations.length} matched.
            </p>
            <Button isLink onClick={this.resetAnnotations}>
              Show all
            </Button>
          </React.Fragment>
        )}
      </EmptyView>
    );
  }

  renderCloseButton() {
    return (
      <div styleName="close">
        <Button
          isText
          className="icon-close"
          data-test="close-results-window"
          styleName="button"
          onClick={this.handleClickOutside}
        />
      </div>
    );
  }

  renderHeader() {
    const { matchedAnnotations, searchTerm } = this.state;
    const isChecked = this.shouldCheckAll();
    const hasCheckboxes =
      matchedAnnotations.length || (!matchedAnnotations.length && searchTerm);

    return (
      <div styleName="header">
        {this.renderCloseButton()}
        <div styleName={cn('sub-header', { 'has-checkboxes': hasCheckboxes })}>
          {hasCheckboxes && (
            <Checkbox
              isSmall
              isChecked={isChecked}
              label="Select all entities"
              styleName="checkbox"
              title="Select all entities"
              onClick={this.handleToggleAll}
            />
          )}
          <Search
            getItems={this.filterItems}
            itemsCount={matchedAnnotations.length}
            placeholder="Search"
            searchTerm={searchTerm}
          />
        </div>
      </div>
    );
  }

  render() {
    const { isActive } = this.state;
    return (
      <HotKeys
        focused
        attach={window}
        handlers={this.keyHandlers}
        keyMap={keyMap}
      >
        <ReactSimpleDropdown
          ref={this.handleRef}
          active={isActive}
          data-test="annotation-results-label"
          styleName="dropdown"
        >
          <DropdownTrigger styleName="trigger" onClick={this.handleToggle}>
            <i className="icon-list" />
            <span>Annotation Results</span>
          </DropdownTrigger>
          <DropdownContent data-test="annotation-results-window">
            {this.renderHeader()}
            {this.renderList()}
            {this.renderEmpty()}
          </DropdownContent>
        </ReactSimpleDropdown>
      </HotKeys>
    );
  }
}

export default onClickOutside(MatchedAnnotationDrawer);
